// project-imports
import { APP_DEFAULT_PATH } from 'config';

// ==============================|| RETURN TO ||============================== //

/** Routes a session must never bounce back into: doing so loops. `/` (the landing page) is matched exactly, below. */
const PUBLIC_PATHS = ['/login', '/auth/callback', '/auth/step-up/callback'];

/**
 * The pages where a sign-in or step-up is being completed. The session is created by the callback request
 * itself, so nothing may probe `/v1/me` (or refresh) there.
 */
export const isOAuthCallbackPath = (pathname: string): boolean => pathname === '/auth/callback' || pathname === '/auth/step-up/callback';

/**
 * Public pages a guest reads: the landing, the legal pages and the documentation. A failed start-up probe of `/v1/me` there
 * means "not signed in", which is normal, so nothing may throw the visitor to the sign-in page.
 */
export const isGuestPage = (pathname: string): boolean =>
  pathname === '/' || pathname === '/docs' || pathname.startsWith('/docs/') || pathname.startsWith('/legal/');

export const isPublicPath = (pathname: string): boolean =>
  // The public landing page: a guest probing `/v1/me` there must not be thrown to /login.
  pathname === '/' || PUBLIC_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

/**
 * Accepts only same-site paths.
 *
 * `returnTo` travels through the query string and back out of the API, so anything that is not a
 * plain absolute path (`https://...`, `//host`, a backslash after the slash, control characters) falls
 * back to the home page rather than being followed.
 */
export const safeReturnTo = (value: string | null | undefined): string => {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    return APP_DEFAULT_PATH;
  }

  if (value.split('').some((char) => char.charCodeAt(0) < 32)) {
    return APP_DEFAULT_PATH;
  }

  if (isPublicPath(value)) {
    return APP_DEFAULT_PATH;
  }

  return value;
};
